// Auth Domain Composable
// Primary interface for views to interact with authentication
// Orchestrates useApiMutation for server state and useAuthStore for client state

import { computed } from 'vue';
import { useRoute } from 'vue-router';
import type { OtpRequestResponse } from '@/domains/auth/types/OtpRequest';
import type { User } from '@/domains/auth/types/User';
import type { LoginResponse } from '@/domains/auth/types/Login';
import { useApiMutation, useApiQuery } from '@/composables/useApi';
import { authEndpoints } from '@/services/api/apiConfig';
import { setLoaderConfig } from '@/core/loading/globalLoadingStore';
import {
  getUserFromCache,
  saveUserToCache,
  clearUserCache,
} from '@/domains/auth/repositories/userRepository';
import { useConstants } from '@/domains/constants';
import { useAuthStore } from '@/domains/auth/stores/useAuthStore';
import { clearRoutesCache } from '@/domains/route';
import { useStoredUserGuid } from '@/composables/useStoredUserGuid';
import { resetRefreshFailedFlag } from '@/services/api/axios';

export const useAuth = () => {
  // === COMPOSABLE STRUCTURE ===

  // Dependencies
  const { state: authState, actions: authActions, status: authStatus } = useAuthStore();
  const { actions: constantsActions } = useConstants();
  const { setUserGuid, clearUserGuid, getUserGuid } = useStoredUserGuid();
  const route = useRoute();

  const requestOtpMutation = useApiMutation<OtpRequestResponse, Error, { uuid: string }, unknown>({
    endpoint: authEndpoints.LOGIN_REQUEST(),
    method: 'post',
  });

  const verifyOtpMutation = useApiMutation<
    LoginResponse,
    Error,
    { verificationToken: string; otp: string },
    unknown
  >({
    endpoint: authEndpoints.VERIFY_OTP(),
    method: 'post',
  });

  const userProfileQuery = useApiQuery<User, Error>({
    endpoint: authEndpoints.GET_ME(),
    enabled: false, // Only fetch when explicitly called via fetchUserProfile
    queryKey: ['userData'],
  });

  const logoutMutation = useApiMutation<unknown, Error, { refreshToken: string }, unknown>({
    endpoint: authEndpoints.LOGOUT(),
    method: 'post',
  });

  // Infrastructure helpers
  // Ensure deviceId is set (used by API headers)
  const ensureDeviceId = (): void => {
    const existingDeviceId = localStorage.getItem('deviceId');
    if (!existingDeviceId) {
      localStorage.setItem('deviceId', crypto.randomUUID());
    }
  };
  const loadUserFromCachedSources = async (skipStore: boolean): Promise<User | null> => {
    if (authState.user) {
      return authState.user;
    }

    const cachedUser = await getUserFromCache();
    if (cachedUser && !skipStore) {
      authActions.setUser(cachedUser);
    }
    return cachedUser;
  };

  const fetchUserFromApi = async (skipStore: boolean, skipCache: boolean): Promise<User> => {
    setLoaderConfig({
      variant: 'card',
      message: 'Loading your profile...',
      size: 'sm',
      backdrop: false,
    });

    const result = await userProfileQuery.refetch();
    if (!result.data) {
      throw new Error('No user data available');
    }

    if (!skipStore) {
      authActions.setUser(result.data);
    }
    if (!skipCache) {
      ensureDeviceId();
      await saveUserToCache(result.data);
    }

    return result.data;
  };

  // === BUSINESS LOGIC ===

  // Business Actions
  const requestOtpWrapper = async (params: { uuid: string }) => {
    // TODO: Add short loader to show that it's doing something
    // Removed blocking loader - allow immediate navigation to OTP entry screen
    // setLoaderConfig({
    //   variant: 'card',
    //   message: 'Sending verification code via SMS...',
    //   size: 'sm',
    //   backdrop: true,
    // });

    try {
      const data = await requestOtpMutation.mutateAsync(params);
      authActions.setVerificationToken(data.verificationToken);
      authActions.setSmsSent(data.smsSent ?? true);
      authActions.setEmailSent(data.emailSent ?? true);
      return data;
    } catch (error) {
      authActions.setError(error instanceof Error ? error.message : 'Failed to request OTP');
      throw error;
    }
  };

  const verifyOtpWrapper = async (params: {
    verificationToken: string;
    otp: string;
    uuid?: string;
  }) => {
    setLoaderConfig({
      variant: 'card',
      message: 'Verifying your code, please wait...',
      size: 'sm',
      backdrop: true,
    });

    try {
      const previousUser = authState.user ?? (await getUserFromCache());
      const previousBrokerCode = previousUser?.brokerCode ?? null;

      const data = await verifyOtpMutation.mutateAsync({
        verificationToken: params.verificationToken,
        otp: params.otp,
      });
      authActions.setVerificationToken(null);

      authActions.login({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        user: data.user,
      });

      ensureDeviceId();
      await saveUserToCache(data.user);

      const newBrokerCode = data.user.brokerCode ?? null;

      const brokerChanged =
        previousBrokerCode !== null &&
        newBrokerCode !== null &&
        previousBrokerCode !== newBrokerCode;

      if (brokerChanged) {
        await Promise.allSettled([clearRoutesCache(), clearUserCache()]);
      }

      let uuidToStore = params.uuid;

      if (!uuidToStore && route.params.uuid && typeof route.params.uuid === 'string') {
        uuidToStore = route.params.uuid;
      }

      if (!uuidToStore) {
        const storedUuid = await getUserGuid();
        if (storedUuid) {
          uuidToStore = storedUuid;
        }
      }

      if (uuidToStore) {
        await setUserGuid(uuidToStore);
      }

      // Reset refresh failed flag after successful login
      resetRefreshFailedFlag();

      await constantsActions.initialize(brokerChanged);
      return data;
    } catch (error) {
      console.error('Error verifying OTP:', error);
      authActions.setError(error instanceof Error ? error.message : 'Invalid OTP code');
      throw error;
    }
  };

  const fetchUserProfileWrapper = async (options?: {
    forceApi?: boolean;
    skipCache?: boolean;
    skipStore?: boolean;
  }) => {
    const forceApi = options?.forceApi ?? false;
    const skipCache = options?.skipCache ?? false;
    const skipStore = options?.skipStore ?? false;
    try {
      if (!forceApi) {
        const cachedUser = await loadUserFromCachedSources(skipStore);
        if (cachedUser) {
          return cachedUser;
        }
      }
      const result = await fetchUserFromApi(skipStore, skipCache);
      return result;
    } catch (error) {
      authActions.setError(error instanceof Error ? error.message : 'Failed to fetch user profile');
      throw error;
    }
  };

  const logoutWrapper = async () => {
    try {
      const refreshToken = authActions.getRefreshToken();
      if (refreshToken) {
        await logoutMutation.mutateAsync({ refreshToken });
      }
    } catch (error) {
      console.warn('Logout API call failed:', error);
    } finally {
      authActions.logout();
      authActions.reset();
      await clearUserGuid(); // Clear stored GUID on logout
      await Promise.allSettled([clearUserCache(), clearRoutesCache()]);
    }
  };

  // === COMPOSABLE OUTPUT ===

  const state = {
    verificationToken: computed(() => authState.verificationToken),
    user: computed(() => authState.user),
    smsSent: computed(() => authState.smsSent),
    emailSent: computed(() => authState.emailSent),
  };

  const selectors = {};

  const actions = {
    requestOtp: requestOtpWrapper,
    verifyOtp: verifyOtpWrapper,
    fetchUserProfile: fetchUserProfileWrapper,
    logout: logoutWrapper,
    clearError: authActions.clearError,
  };

  const status = {
    isLoading: computed(
      () =>
        requestOtpMutation.isPending.value ||
        verifyOtpMutation.isPending.value ||
        userProfileQuery.isLoading.value ||
        logoutMutation.isPending.value
    ),
    error: computed(() => authStatus.error),
    loading: computed(() => authStatus.loading),
    isReconnecting: computed(() => authStatus.isReconnecting),
  };

  return {
    state,
    selectors,
    actions,
    status,
  };
};
